import type { StyleSpecification } from 'maplibre-gl'
import type { BasemapStyle } from './types'

/*
 * Pieces shared by every street-basemap provider, plus the satellite view
 * (which is Esri for all of them — no provider has a reason to differ).
 */

/**
 * True when `url`'s host is `domain` or a subdomain of it.
 *
 * Deliberately not a substring test. `url.includes('cartocdn.com')` also
 * matches `https://evil.example/?x=cartocdn.com` and
 * `https://cartocdn.com.evil.example/`, and the CARTO branch of this check
 * decides whether to append an API key — so a substring match would hand the
 * key to any host that merely mentions the domain. Style documents are fetched
 * from third parties and list tile URLs, so those strings are not ours to
 * trust.
 *
 * Resolved without a base URL on purpose, so relative URLs fail to parse and
 * return false. Tile, style, sprite and glyph URLs are always absolute, and a
 * same-origin relative URL is never a third-party provider — so it should
 * never be handed a provider credential.
 */
export function matchesHost(url: string, domain: string): boolean {
  let host: string
  try {
    host = new URL(url).hostname.toLowerCase()
  } catch {
    return false
  }
  return host === domain || host.endsWith(`.${domain}`)
}

/** Page surface colors — the map background must match so panning past the
 *  edge of loaded tiles doesn't flash a foreign color. */
export const SURFACE_DARK = '#0B0F0E'
export const SURFACE_LIGHT = '#F6F4EE'

const ESRI_IMAGERY =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

export const ESRI_ATTRIBUTION =
  'Imagery © <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a>, Maxar, Earthstar Geographics'

/** Esri World Imagery is reliably available to ~19 globally; past that the map
 *  shows "Map data not yet available" placeholders, so cap there. */
export const ESRI_MAX_ZOOM = 19

/**
 * Vector basemaps are built to zoom 14 and overzoomed past that — the tiles
 * keep scaling cleanly (labels and strokes stay crisp because they're drawn
 * client-side), so the map can still go deeper than the source maxzoom.
 */
export const VECTOR_MAX_ZOOM = 20

export function buildRasterStyle(
  tileUrl: string,
  backgroundColor: string,
  maxzoom: number,
): StyleSpecification {
  return {
    version: 8,
    sources: {
      basemap: {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
        maxzoom,
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': backgroundColor } },
      { id: 'basemap', type: 'raster', source: 'basemap' },
    ],
  }
}

/*
 * Satellite alternative — USGS imagery.
 *
 * Esri World Imagery is keyless and unwatermarked today, but so was CARTO's
 * raster service until it wasn't. This is the escape hatch if that changes:
 * set VITE_SATELLITE_PROVIDER=usgs and redeploy. Like the street providers,
 * there is no automatic switching — it is a supervised config change.
 *
 * USGS was picked over the global alternatives on licensing. It is a U.S.
 * government work in the public domain: no key, no quota, no attribution
 * obligation beyond courtesy, and no terms that can be revised against us.
 * Sentinel-2 cloudless (EOX) covers the whole world but documents
 * rate-limiting and is vague on commercial use.
 *
 * The cost is that USGS is U.S.-only and stops at zoom 16. That matches
 * GeoBorder's coverage model — detailed output is U.S. and territories,
 * non-U.S. points get country-level context — so imagery survives exactly
 * where the product has something to say.
 */
const USGS_IMAGERY =
  'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'

export const USGS_ATTRIBUTION =
  'Imagery courtesy of the <a href="https://www.usgs.gov/" target="_blank" rel="noopener">U.S. Geological Survey</a>'

/** USGS serves to 16; past that it 404s rather than overzooming. */
const USGS_MAX_ZOOM = 16

const useUsgs =
  (import.meta.env.VITE_SATELLITE_PROVIDER ?? '').trim().toLowerCase() === 'usgs'

/** Satellite view — identical across street providers. */
export function satelliteStyle(): BasemapStyle {
  if (useUsgs) {
    return {
      style: buildRasterStyle(USGS_IMAGERY, SURFACE_DARK, USGS_MAX_ZOOM),
      attribution: USGS_ATTRIBUTION,
      maxZoom: USGS_MAX_ZOOM,
    }
  }
  return {
    style: buildRasterStyle(ESRI_IMAGERY, SURFACE_DARK, ESRI_MAX_ZOOM),
    attribution: ESRI_ATTRIBUTION,
    maxZoom: ESRI_MAX_ZOOM,
  }
}
